/**
 * Shapes shared by the generator, the solver, and the UI.
 *
 * The board is 10×10 with two stars per line, always. That is not a default with
 * other options behind it: two stars needs 2n of them, the largest set of cells
 * none of which touch in an n×n grid is ceil(n/2)², and so nothing under 9×9 has
 * a legal placement at all — 8×8 has exactly two, which would make every board
 * the same puzzle. Above 10×10 a board is still buildable but generation becomes
 * a minute-long search for a board whose difficulty we then cannot control. So
 * the size is a constant, and these two are the only place it is written down.
 */

export const SIZE = 10;
export const STARS = 2;
export const CELLS = SIZE * SIZE;

export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';

export const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard', 'expert'];

export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  expert: 'Expert',
};

/** A generated board. `solution` is kept for the reveal, never for checking. */
export interface Puzzle {
  /** Region id (0..SIZE-1) for every cell, row-major. */
  regions: number[];
  /** Cell indices holding a star in the puzzle's unique solution. */
  solution: number[];
  /** The difficulty the generator actually achieved, which can miss the ask. */
  difficulty: Difficulty;
  seed: number;
}

/** What the player has put in a cell. */
export const EMPTY = 0;
export const DOT = 1; // the grey ✕
export const STAR = 2;

export type Mark = typeof EMPTY | typeof DOT | typeof STAR;

/**
 * What a placed star is drawn as.
 *
 * Purely what the player sees: the rules, the solver, and the saved marks all
 * speak of stars whichever of these is chosen, and switching mid-puzzle changes
 * nothing but the picture in the cell.
 */
export type Glyph = 'star' | 'cat';

export const GLYPHS: readonly Glyph[] = ['star', 'cat'];

/** Singular and plural, for the prose that has to name the thing. */
export const GLYPH_NOUN: Record<Glyph, { one: string; many: string }> = {
  star: { one: 'star', many: 'stars' },
  cat: { one: 'cat', many: 'cats' },
};
